import {
    Dialog,
    Box,
    DialogTitle,
    DialogContent,
    TextField,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    Checkbox,
    ListItemText,
    Typography,
    lighten,
} from "@mui/material";
import CustomButton from "./default/CustomButton";
import Loading from "./Loading";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { IChapter } from "../types/chapter";
import axiosService from "../utils/axios";
import { addChapterIngredientTransform } from "../utils/helpers/recipeHelper";
import { fetchChapters as fetchChaptersHelper } from "../utils/helpers/chapterHelper";
import type { IRecipe, ISavedRecipes } from "../types/recipe";
import type { RootState } from "../store";
import { useSelector } from "react-redux";
import Theme from "../utils/theme";

interface IChapterAddDialog {
    setOpenModal: (b: boolean) => void;
    recipe: IRecipe | ISavedRecipes;
    saveable: boolean;
}

const ChapterAddDialog: React.FC<IChapterAddDialog> = ({ setOpenModal, recipe, saveable = false }) => {
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const [chapterName, setChapterName] = useState("");
    const [createChapterModal, setCreateChapterModal] = useState(false);
    const [chapters, setChapters] = useState<IChapter[]>([]);
    const [chapterLoading, setChapterLoading] = useState<Set<number>>(new Set());
    const [localOpenModal, setLocalOpenModal] = useState(false);
    const [localRecipeId, setLocalRecipeId] = useState<string | null>(recipe.hasOwnProperty("_id") ? (recipe as ISavedRecipes)._id : null);

    const handleCloseModal = useCallback(() => {
        setOpenModal(false);
        setTimeout(() => setCreateChapterModal(false), 300);
    }, [setOpenModal]);

    const fetchRecipeId = useCallback(() => {
        axiosService
            .get(`${process.env.REACT_APP_API_URL}/recipes/recipeIdFromGenerationId`, { params: { generationId: recipe.generationId } })
            .then((res) => {
                if (res.data.body) setLocalRecipeId(res.data.body?._id);
            });
    }, [recipe.generationId]);

    const fetchChapters = useCallback(() => {
        fetchChaptersHelper().then((res) => {
            setChapters(res.data.body.chapters);
            setLocalOpenModal(true);
        });
    }, []);

    useEffect(() => {
        fetchChapters();
    }, [fetchChapters]);

    useEffect(() => {
        if (localRecipeId || !recipe.hasOwnProperty("generationId")) return;
        fetchRecipeId();
    });

    const createChapter = useCallback(() => {
        axiosService.post(`${process.env.REACT_APP_API_URL}/chapters/create`, { name: chapterName }).then(() => {
            fetchChapters();
            setCreateChapterModal(false);
        });
    }, [chapterName, fetchChapters]);

    const saveRecipe = useCallback(() => {
        axiosService
            .post(`${process.env.REACT_APP_API_URL}/recipes/create`, { ...recipe, tags: { old: recipe.tags } })
            .then(() => setOpenModal(false));
    }, [recipe, setOpenModal]);

    const handleChapterToggle = useCallback(
        (index: number) => () => {
            setChapterLoading((prev) => new Set(prev).add(index));

            const included = localRecipeId ? chapters[index].recipes.includes(localRecipeId) : false;
            const transformedRecipe = addChapterIngredientTransform(recipe);

            axiosService
                .post(`${process.env.REACT_APP_API_URL}/chapters/addRecipe`, {
                    ...transformedRecipe,
                    chapterId: chapters[index]._id,
                    recipeId: localRecipeId,
                    tags: { old: transformedRecipe.tags, new: [] },
                    included,
                })
                .then((res) => {
                    setChapters((prev) =>
                        prev.map((chapter, i) => (i === index ? { ...chapter, recipes: res.data.body.chapterRecipes } : chapter))
                    );
                    setLocalRecipeId(res.data.body.recipeId);

                    setChapterLoading((prev) => {
                        const updated = new Set(prev);
                        updated.delete(index);
                        return updated;
                    });
                });
        },
        [localRecipeId, chapters, recipe]
    );

    return (
        <Dialog open={localOpenModal} onClose={handleCloseModal}>
            {createChapterModal ? (
                <Box>
                    <DialogTitle sx={{ textAlign: "center" }}>Create Chapter</DialogTitle>
                    <DialogContent dividers>
                        <TextField autoFocus onChange={(e) => setChapterName(e.target.value)} label="Chapter Name" variant="standard" />
                        <Divider />
                        <CustomButton onClick={createChapter} label="Create Chapter" sx={{ color: "white", width: "100%", marginTop: 2 }} />
                    </DialogContent>
                </Box>
            ) : (
                <Box sx={{ minWidth: "250px" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <DialogTitle>Save to...</DialogTitle>
                        {saveable && (
                            <CustomButton
                                onClick={saveRecipe}
                                sx={{
                                    color: "white",
                                    position: "absolute",
                                    right: "15px",
                                    backgroundColor: !!localRecipeId
                                        ? lighten(theme.palette.text.secondary, 0.5)
                                        : theme.palette.primary.main,
                                }}
                                label={!!localRecipeId ? "Saved" : "Save Recipe"}
                                disabled={!!localRecipeId}
                            />
                        )}
                    </Box>

                    <DialogContent dividers>
                        {chapters.length > 0 ? (
                            <List sx={{ paddingTop: 0 }}>
                                {chapters.map((chapter, index) => (
                                    <ListItem key={chapter._id} onClick={handleChapterToggle(index)} sx={{ cursor: "pointer" }}>
                                        <ListItemIcon>
                                            {chapterLoading.has(index) ? (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        width: "100%",
                                                    }}
                                                >
                                                    <Loading size="20px" />
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        width: "100%",
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={chapter.recipes.includes(localRecipeId!)}
                                                        tabIndex={-1}
                                                        disableRipple
                                                    />
                                                </Box>
                                            )}
                                        </ListItemIcon>
                                        <ListItemText primary={chapter.name} />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Box sx={{ paddingBottom: 2, textAlign: "center" }}>
                                <Typography>No chapters found.</Typography>
                            </Box>
                        )}
                        <Divider />
                        <CustomButton
                            onClick={() => setCreateChapterModal(true)}
                            label="Create Chapter"
                            sx={{ color: "white", width: "100%", marginTop: 2 }}
                        />
                    </DialogContent>
                </Box>
            )}
        </Dialog>
    );
};

export default ChapterAddDialog;
